
import { Job } from '../../../lib/supabase';

interface JobCardProps {
  job: Job;
  isSelected: boolean;
  onClick: () => void;
}

export default function JobCard({ job, isSelected, onClick }: JobCardProps) {
  // Calculate time posted
  const getTimePosted = (createdAt: string) => {
    const now = new Date();
    const posted = new Date(createdAt);
    const diffMs = now.getTime() - posted.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  // Truncate description to 2 lines
  const truncateDescription = (text: string | null, maxLength: number = 120) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  return (
    <div
      onClick={onClick}
      className={`bg-[#1c1c1c] border rounded cursor-pointer transition-all card-hover ${
        isSelected
          ? 'border-[#6366f1] shadow-lg'
          : 'border-[#333333]'
      }`}
    >
      <div className="p-6">
        {/* Job Title & Company */}
        <div className="mb-3">
          <h3 className="text-lg font-semibold text-[#6366f1] hover:underline mb-1">
            {job.title}
          </h3>
          <div className="flex items-center gap-2">
            <span className="text-white font-medium">{job.company}</span>
            <div className="flex items-center text-sm text-[#a0a0a0]">
              <i className="ri-star-fill text-yellow-500 mr-1"></i>
              <span>4.{Math.floor(Math.random() * 5 + 3)}</span>
            </div>
          </div>
        </div>

        {/* Location & Badges */}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <span className="text-sm text-[#a0a0a0]">
            <i className="ri-map-pin-line mr-1"></i>
            {job.location}
          </span>
          {job.job_type === 'Remote' && (
            <span className="px-2 py-1 bg-green-500/10 border border-green-500/30 text-green-400 text-xs rounded whitespace-nowrap">
              <i className="ri-home-office-line mr-1"></i>
              Remote
            </span>
          )}
          {job.job_type === 'Hybrid' && (
            <span className="px-2 py-1 bg-blue-500/10 border border-blue-500/30 text-blue-400 text-xs rounded whitespace-nowrap">
              <i className="ri-building-line mr-1"></i>
              Hybrid
            </span>
          )}
        </div>

        {/* Salary */}
        <div className="mb-3">
          <span className="text-white font-semibold text-base">
            {job.salary_range}
          </span>
        </div>

        {/* Description Preview */}
        <p className="text-sm text-[#a0a0a0] leading-relaxed mb-3">
          {truncateDescription(job.description)}
        </p>

        {/* Tags */}
        {job.tags && job.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3">
            {job.tags.slice(0, 3).map((tag, index) => (
              <span
                key={index}
                className="px-2 py-1 bg-[#2d2d2d] text-[#a0a0a0] text-xs rounded border border-[#333333]"
              >
                {tag}
              </span>
            ))}
            {job.tags.length > 3 && (
              <span className="px-2 py-1 text-[#a0a0a0] text-xs">
                +{job.tags.length - 3}
              </span>
            )}
          </div>
        )}

        {/* Footer - Time Posted */}
        <div className="pt-3 border-t border-[#333333]">
          <span className="text-xs text-[#a0a0a0]">
            <i className="ri-time-line mr-1"></i>
            {getTimePosted(job.created_at)}
          </span>
        </div>
      </div>
    </div>
  );
}
